// src/routes/domains/canada/canada.routes.tsx
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Lazy imports – paths based on original App.tsx
// ------------------------------

// Core assessments and tools
const CanadaExpressEntryAssessment = lazy(() => import('@/pages/CanadaExpressEntryAssessment'));
const CanadaFamilySponsorshipAssessment = lazy(() => import('@/pages/FamilySponsorshipAssessment'));
const CanadaQuebecSelectionAssessment = lazy(() => import('@/pages/QuebecSelectionAssessment'));
const CanadaAtlanticProgramAssessment = lazy(() => import('@/pages/AtlanticProgramAssessment'));
const CanadaRuralNorthernAssessment = lazy(() => import('@/pages/RuralNorthernAssessment'));
const CanadaStartUpVisaAssessment = lazy(() => import('@/pages/StartUpVisaAssessment'));
const CanadaStudyPermitAssessment = lazy(() => import('@/pages/StudyPermitAssessment'));
const CanadaTemporaryWorkerAssessment = lazy(() => import('@/pages/TemporaryWorkerAssessment'));
const CanadaCaregiverAssessment = lazy(() => import('@/pages/CanadaCaregiverAssessment'));
const CanadaAgriFoodPilotAssessment = lazy(() => import('@/pages/AgriFoodPilotAssessment'));
const CanadaSelfEmployedAssessment = lazy(() => import('@/pages/CanadaSelfEmployedAssessment'));
// Locked-template assessments (hyphen paths used by AssessmentOS PATHWAY_ROUTES)
const CanadaPNPAssessmentLocked = lazy(() => import('@/pages/CanadaPNPAssessment'));
const CanadaPNPFinder = lazy(() => import('@/pages/CanadaPNPFinder'));
const CanadaFamilyAssessmentLocked = lazy(() => import('@/pages/CanadaFamilyAssessment'));
const CanadaQuebecAssessmentLocked = lazy(() => import('@/pages/CanadaQuebecAssessment'));
const CanadaAtlanticAssessmentLocked = lazy(() => import('@/pages/CanadaAtlanticAssessment'));
const CanadaRNIPAssessmentLocked = lazy(() => import('@/pages/CanadaRNIPAssessment'));
const CanadaStartupAssessmentLocked = lazy(() => import('@/pages/CanadaStartupAssessment'));
const CanadaStudyAssessmentLocked = lazy(() => import('@/pages/CanadaStudyAssessment'));
const CanadaWorkerAssessmentLocked = lazy(() => import('@/pages/CanadaWorkerAssessment'));
const CanadaAgriFoodAssessmentLocked = lazy(() => import('@/pages/CanadaAgriFoodAssessment'));
const CanadaPathwayEngine = lazy(() => import('@/pages/CanadaPathwayEngine'));
const CanadaTimelineForecaster = lazy(() => import('@/pages/CanadaTimelineForecaster'));
const CanadaSuccessPredictionEngine = lazy(() => import('@/pages/CanadaSuccessPredictionEngine'));
const CanadaRiskAssessment = lazy(() => import('@/pages/CanadaRiskAssessment'));
const CanadaJourneyMap = lazy(() => import('@/pages/CanadaJourneyMap'));
const CanadaSavedJourneyMaps = lazy(() => import('@/pages/CanadaSavedJourneyMaps'));
const CanadaToolsEcosystem = lazy(() => import('@/pages/canada-premium/CanadaToolsEcosystem'));
const CanadaPrCompanion = lazy(() => import('@/pages/journey/CanadaPrCompanion'));
const CanadaCompanionResults = lazy(() => import('@/pages/journey/CanadaCompanionResults'));
const CanadaVisaFormConductor = lazy(() => import('@/pages/CanadaVisaFormConductor'));
const CanadaEvisaGateway = lazy(() => import('@/pages/CanadaEvisaGateway'));
const CanadaDependencyCommander = lazy(() => import('@/pages/canada-family/CanadaDependencyCommander'));

// Additional Canada tools
const CanadaExpressEntryCRSCalculator = lazy(() => import('@/pages/CanadaExpressEntryCRSCalculator'));
const CanadaProtectionAssessment = lazy(() => import('@/pages/CanadaProtectionAssessment'));
const CanadaFSWEligibilityChecker = lazy(() => import('@/pages/CanadaFSWEligibilityChecker'));
const CanadaCECChecker = lazy(() => import('@/pages/CanadaCECChecker'));
const CanadaFSTPEligibility = lazy(() => import('@/pages/CanadaFSTPEligibility'));
const CanadaDocumentCompletenessChecker = lazy(() => import('@/pages/CanadaDocumentCompletenessChecker'));
const CanadaTranslationGuide = lazy(() => import('@/pages/CanadaTranslationGuide'));
const CanadaECATool = lazy(() => import('@/pages/CanadaECATool'));
const CanadaCLBConverter = lazy(() => import('@/pages/CanadaCLBConverter'));
const CanadaWorkRefLetterChecker = lazy(() => import('@/pages/CanadaWorkRefLetterChecker'));
const CanadaTaxEstimator = lazy(() => import('@/pages/CanadaTaxEstimator'));
const CanadaHealthCoverageChecker = lazy(() => import('@/pages/CanadaHealthCoverageChecker'));
const CanadaCitizenshipEligibility = lazy(() => import('@/pages/CanadaCitizenshipEligibility'));
const CanadaProvinceComparisonTool = lazy(() => import('@/pages/CanadaProvinceComparisonTool'));
const CanadaLMIAEmployerDatabase = lazy(() => import('@/pages/CanadaLMIAEmployerDatabase'));
const CanadaJobBankIntegration = lazy(() => import('@/pages/CanadaJobBankIntegration'));
const CanadaBusinessVisitorCETAGuide = lazy(() => import('@/pages/CanadaBusinessVisitorCETAGuide'));
const CanadaQuebecSkilledWorkerCalculator = lazy(() => import('@/pages/CanadaQuebecSkilledWorkerCalculator'));
const CanadaExpressEntryDrawPredictor = lazy(() => import('@/pages/CanadaExpressEntryDrawPredictor'));
const CanadaPNPProcessingTimes = lazy(() => import('@/pages/CanadaPNPProcessingTimes'));
const CanadaIRCCWebformHelper = lazy(() => import('@/pages/CanadaIRCCWebformHelper'));
const CanadaPanelPhysicianFinder = lazy(() => import('@/pages/CanadaPanelPhysicianFinder'));
const CanadaPRCardPhotoValidator = lazy(() => import('@/pages/CanadaPRCardPhotoValidator'));
const CanadaITASimulator = lazy(() => import('@/pages/CanadaITASimulator'));
const CanadaImmigrationEligibilityQuiz = lazy(() => import('@/pages/CanadaImmigrationEligibilityQuiz'));
const CanadaComprehensivePointsCalculator = lazy(() => import('@/pages/CanadaComprehensivePointsCalculator'));
const CanadaMaintenanceFundsCalculator = lazy(() => import('@/pages/CanadaMaintenanceFundsCalculator'));
const CanadaDependentVisaCalculator = lazy(() => import('@/pages/CanadaDependentVisaCalculator'));
const CanadaFinancialMaintenanceTools = lazy(() => import('@/pages/CanadaFinancialMaintenanceTools'));
const CanadaDLIValidator = lazy(() => import('@/pages/CanadaDLIValidator'));
const CanadaAccommodationChecker = lazy(() => import('@/pages/CanadaAccommodationChecker'));
const CanadaBiometricValidator = lazy(() => import('@/pages/CanadaBiometricValidator'));
const CanadaPathwayAnalyticsDashboard = lazy(() => import('@/pages/CanadaPathwayAnalyticsDashboard'));
const CanadaPathwayDreamsEngine = lazy(() => import('@/pages/CanadaPathwayDreamsEngine'));
const CanadaOccupationDemandChecker = lazy(() => import('@/pages/CanadaOccupationDemandChecker'));
const CanadaStartupVisaPlanner = lazy(() => import('@/pages/CanadaStartupVisaPlanner'));
const CanadaImmigrationTechData = lazy(() => import('@/pages/CanadaImmigrationTechData'));
const CanadaYouthStudentTools = lazy(() => import('@/pages/CanadaYouthStudentTools'));
const CanadaProfessionalHealthcareRoutes = lazy(() => import('@/pages/CanadaProfessionalHealthcareRoutes'));
const CanadaAccommodationHousing = lazy(() => import('@/pages/CanadaAccommodationHousing'));
const CanadaDependantFamilyTools = lazy(() => import('@/pages/CanadaDependantFamilyTools'));
const CanadaVisaExpiryExtensionTools = lazy(() => import('@/pages/CanadaVisaExpiryExtensionTools'));
const CanadaPermanentResidenceTools = lazy(() => import('@/pages/CanadaPermanentResidenceTools'));
const CanadaGlobalSalaryCalculator = lazy(() => import('@/pages/CanadaGlobalSalaryCalculator'));
const CanadaVisaFeeCalculator = lazy(() => import('@/pages/CanadaVisaFeeCalculator'));

// Honest requirement guides (no fake OCR validate API)
const CanadaPoliceValidator = lazy(() => import('@/pages/CanadaDocumentRequirementGuide'));
const CanadaMarriageValidator = lazy(() => import('@/pages/CanadaDocumentRequirementGuide'));
const CanadaEducationValidator = lazy(() => import('@/pages/CanadaDocumentRequirementGuide'));
const CanadaVerificationDashboard = lazy(() => import('@/pages/CanadaDocumentRequirementGuide'));
const CanadaBankValidator = lazy(() => import('@/pages/CanadaDocumentRequirementGuide'));
const CanadaDocumentAuthentication = lazy(() => import('@/pages/CanadaDocumentAuthentication'));
const CanadaAssessmentResultAnalysis = lazy(() => import('@/pages/CanadaAssessmentResultAnalysis'));

// ------------------------------
// Canada Routes Definition
// ------------------------------
export const canadaRoutes: AppRoute[] = [
  // Express Entry & CRS
  {
    id: 'canada.express_entry_assessment',
    domain: 'canada',
    path: '/express-entry-assessment',
    element: createProtectedRoute(<CanadaExpressEntryAssessment />, { tier: 'premium', meta: { title: 'Express Entry Assessment', region: 'CA', intent: 'assessment' } }),
  },
  {
    // Restored — this path was only registered in core.routes.tsx (removed by an
    // over-broad duplicate-route cleanup that matched by component name only, not
    // path, and didn't notice this component had two genuinely different URLs).
    // The Canada tools ecosystem page links here specifically.
    id: 'canada.express_entry_assessment_alt',
    domain: 'canada',
    path: '/canada-express-entry-assessment',
    element: createProtectedRoute(<CanadaExpressEntryAssessment />, { tier: 'premium', meta: { title: 'Express Entry Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-ee-assessment', category: 'assessments' } }),
  },
  {
    id: 'canada.crs_calculator',
    domain: 'canada',
    path: '/canada-express-entry-crs',
    element: createProtectedRoute(<CanadaExpressEntryCRSCalculator />, { tier: 'premium', meta: { title: 'CRS Calculator', region: 'CA', intent: 'calculator', toolId: 'ca-express-entry-crs', category: 'calculators' } }),
  },
  {
    id: 'canada.protection_assessment',
    domain: 'canada',
    path: '/canada-protection-assessment',
    element: createProtectedRoute(<CanadaProtectionAssessment />, {
      protected: true,
      meta: { title: 'Refugee & Humanitarian — Canada', region: 'CA', intent: 'assessment', toolId: 'ca-protection', category: 'assessments' },
    }),
  },
  {
    id: 'canada.pnp_assessment',
    domain: 'canada',
    path: '/canada/pnp-finder',
    element: <Navigate to="/canada-pnp-finder" replace />,
  },
  {
    id: 'canada.pnp_finder',
    domain: 'canada',
    path: '/canada-pnp-finder',
    element: createProtectedRoute(<CanadaPNPFinder />, {
      tier: 'premium',
      meta: {
        title: 'PNP Finder',
        region: 'CA',
        intent: 'assessment',
        toolId: 'ca-pnp-finder',
        category: 'assessments',
      },
    }),
  },
  // AssessmentOS PATHWAY_ROUTES.CA — hyphen paths (single owner)
  {
    id: 'canada.pnp_assessment_os',
    domain: 'canada',
    path: '/canada-pnp-assessment',
    element: createProtectedRoute(<CanadaPNPAssessmentLocked />, { tier: 'premium', meta: { title: 'PNP Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-pnp-assessment', category: 'assessments' } }),
  },
  {
    id: 'canada.family_assessment_os',
    domain: 'canada',
    path: '/canada-family-assessment',
    element: createProtectedRoute(<CanadaFamilyAssessmentLocked />, { tier: 'premium', meta: { title: 'Family Sponsorship Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-family-sponsor', category: 'planning' } }),
  },
  {
    id: 'canada.quebec_assessment_os',
    domain: 'canada',
    path: '/canada-quebec-assessment',
    element: createProtectedRoute(<CanadaQuebecAssessmentLocked />, { tier: 'premium', meta: { title: 'Quebec Selection Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-quebec-selection', category: 'assessments' } }),
  },
  {
    id: 'canada.atlantic_assessment_os',
    domain: 'canada',
    path: '/canada-atlantic-assessment',
    element: createProtectedRoute(<CanadaAtlanticAssessmentLocked />, { tier: 'premium', meta: { title: 'Atlantic Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-atlantic', category: 'assessments' } }),
  },
  {
    id: 'canada.rnip_assessment_os',
    domain: 'canada',
    path: '/canada-rnip-assessment',
    element: createProtectedRoute(<CanadaRNIPAssessmentLocked />, { tier: 'premium', meta: { title: 'RNIP Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-rnip-assessment', category: 'assessments' } }),
  },
  {
    id: 'canada.startup_assessment_os',
    domain: 'canada',
    path: '/canada-startup-assessment',
    element: createProtectedRoute(<CanadaStartupAssessmentLocked />, { tier: 'premium', meta: { title: 'Start-up Visa Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-start-up-visa', category: 'assessments' } }),
  },
  {
    id: 'canada.study_assessment_os',
    domain: 'canada',
    path: '/canada-study-assessment',
    element: createProtectedRoute(<CanadaStudyAssessmentLocked />, { tier: 'premium', meta: { title: 'Study Permit Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-study-permit', category: 'planning' } }),
  },
  {
    id: 'canada.worker_assessment_os',
    domain: 'canada',
    path: '/canada-worker-assessment',
    element: createProtectedRoute(<CanadaWorkerAssessmentLocked />, { tier: 'premium', meta: { title: 'Temporary Worker Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-temporary-worker2', category: 'assessments' } }),
  },
  {
    id: 'canada.agrifood_assessment_os',
    domain: 'canada',
    path: '/canada-agrifood-assessment',
    element: createProtectedRoute(<CanadaAgriFoodAssessmentLocked />, { tier: 'premium', meta: { title: 'Agri-Food Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-agri-food', category: 'assessments' } }),
  },
  {
    id: 'canada.fsw_eligibility',
    domain: 'canada',
    path: '/canada-fsw-eligibility',
    element: createProtectedRoute(<CanadaFSWEligibilityChecker />, { tier: 'premium', meta: { title: 'FSW Eligibility', region: 'CA', intent: 'checker', toolId: 'ca-fsw-eligibility', category: 'assessments' } }),
  },
  {
    id: 'canada.cec_checker',
    domain: 'canada',
    path: '/canada-cec-checker',
    element: createProtectedRoute(<CanadaCECChecker />, { tier: 'premium', meta: { title: 'CEC Checker', region: 'CA', intent: 'checker', toolId: 'ca-cec-checker', category: 'assessments' } }),
  },
  {
    id: 'canada.fstp_eligibility',
    domain: 'canada',
    path: '/canada-fstp-eligibility',
    element: createProtectedRoute(<CanadaFSTPEligibility />, { tier: 'premium', meta: { title: 'FSTP Eligibility', region: 'CA', intent: 'checker', toolId: 'ca-fstp-eligibility', category: 'assessments' } }),
  },
  {
    id: 'canada.family_sponsorship',
    domain: 'canada',
    path: '/canada/family-sponsor',
    element: createProtectedRoute(<CanadaFamilySponsorshipAssessment />, { tier: 'premium', meta: { title: 'Family Sponsorship', region: 'CA', intent: 'assessment' } }),
  },
  {
    id: 'canada.quebec_selection',
    domain: 'canada',
    path: '/canada/quebec-selection',
    element: createProtectedRoute(<CanadaQuebecSelectionAssessment />, { tier: 'premium', meta: { title: 'Quebec Selection', region: 'CA', intent: 'assessment' } }),
  },
  {
    // CanadaPEQAssessment.tsx archived — old "Quantum"-styled duplicate of
    // CanadaQuebecAssessment (the honest, UPRM-wired "locked template" version).
    id: 'canada.peq_assessment',
    domain: 'canada',
    path: '/canada-peq-assessment',
    element: <Navigate to="/canada-quebec-assessment" replace />,
  },
  {
    id: 'canada.atlantic_program',
    domain: 'canada',
    path: '/canada/atlantic',
    element: createProtectedRoute(<CanadaAtlanticProgramAssessment />, { tier: 'premium', meta: { title: 'Atlantic Program', region: 'CA', intent: 'assessment' } }),
  },
  {
    // CanadaAIPEligibility.tsx archived — old "Quantum"-styled duplicate of
    // CanadaAtlanticAssessment (the honest, UPRM-wired "locked template" version).
    id: 'canada.aip_eligibility',
    domain: 'canada',
    path: '/canada-aip-eligibility',
    element: <Navigate to="/canada-atlantic-assessment" replace />,
  },
  {
    id: 'canada.rnip_assessment',
    domain: 'canada',
    path: '/canada/rnip',
    element: createProtectedRoute(<CanadaRuralNorthernAssessment />, { tier: 'premium', meta: { title: 'RNIP Assessment', region: 'CA', intent: 'assessment' } }),
  },
  {
    // CanadaRNIPEligibility.tsx archived — old "Quantum"-styled duplicate of
    // CanadaRNIPAssessment (the honest, UPRM-wired "locked template" version).
    id: 'canada.rnip_eligibility',
    domain: 'canada',
    path: '/canada-rnip-eligibility',
    element: <Navigate to="/canada-rnip-assessment" replace />,
  },
  {
    id: 'canada.start_up_visa',
    domain: 'canada',
    path: '/canada/start-up-visa',
    element: createProtectedRoute(<CanadaStartUpVisaAssessment />, { tier: 'premium', meta: { title: 'Start-Up Visa', region: 'CA', intent: 'assessment' } }),
  },
  {
    id: 'canada.study_permit',
    domain: 'canada',
    path: '/canada/study-permit',
    element: createProtectedRoute(<CanadaStudyPermitAssessment />, { tier: 'premium', meta: { title: 'Study Permit', region: 'CA', intent: 'assessment' } }),
  },
  {
    id: 'canada.temporary_worker',
    domain: 'canada',
    path: '/canada/temporary-worker',
    element: createProtectedRoute(<CanadaTemporaryWorkerAssessment />, { tier: 'premium', meta: { title: 'Temporary Worker', region: 'CA', intent: 'assessment' } }),
  },
  {
    id: 'canada.caregiver',
    domain: 'canada',
    path: '/canada/caregiver',
    element: createProtectedRoute(<CanadaCaregiverAssessment />, { tier: 'premium', meta: { title: 'Caregiver Assessment', region: 'CA', intent: 'assessment' } }),
  },
  {
    // Restored — this path was only registered in core.routes.tsx (removed by an
    // over-broad duplicate-route cleanup that matched by component name only, not
    // path, and didn't notice this component had two genuinely different URLs).
    // The Canada tools ecosystem page links here specifically, and canada.caregiver_programs
    // below redirects here.
    id: 'canada.caregiver_assessment_alt',
    domain: 'canada',
    path: '/canada-caregiver-assessment',
    element: createProtectedRoute(<CanadaCaregiverAssessment />, { tier: 'premium', meta: { title: 'Caregiver Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-caregiver', category: 'assessments' } }),
  },
  {
    id: 'canada.agri_food_assessment',
    domain: 'canada',
    path: '/canada/agri-food',
    element: createProtectedRoute(<CanadaAgriFoodPilotAssessment />, { tier: 'premium', meta: { title: 'Agri-Food Pilot', region: 'CA', intent: 'assessment' } }),
  },
  {
    // CanadaAgriFoodPilot.tsx archived — old "Quantum"-styled duplicate of
    // CanadaAgriFoodAssessment (the honest, UPRM-wired "locked template" version).
    id: 'canada.agri_food_pilot',
    domain: 'canada',
    path: '/canada-agrifood-pilot',
    element: <Navigate to="/canada-agrifood-assessment" replace />,
  },
  {
    // CanadaCaregiverPrograms.tsx archived — old "Quantum"-styled duplicate of
    // CanadaCaregiverAssessment (the honest, UPRM-wired "locked template" version).
    id: 'canada.caregiver_programs',
    domain: 'canada',
    path: '/canada-caregiver-programs',
    element: <Navigate to="/canada-caregiver-assessment" replace />,
  },
  {
    id: 'canada.self_employed_assessment',
    domain: 'canada',
    path: '/canada/self-employed',
    element: createProtectedRoute(<CanadaSelfEmployedAssessment />, { tier: 'premium', meta: { title: 'Self-Employed Assessment', region: 'CA', intent: 'assessment' } }),
  },
  {
    // Restored — this path was only registered in core.routes.tsx (removed by an
    // over-broad duplicate-route cleanup that matched by component name only, not
    // path, and didn't notice this component had two genuinely different URLs).
    // The Canada tools ecosystem page links here specifically, and canada.self_employed_program
    // below redirects here.
    id: 'canada.self_employed_assessment_alt',
    domain: 'canada',
    path: '/canada-self-employed-assessment',
    element: createProtectedRoute(<CanadaSelfEmployedAssessment />, { tier: 'premium', meta: { title: 'Self-Employed Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-self-employed', category: 'assessments' } }),
  },
  {
    // CanadaSelfEmployedProgram.tsx archived — old "Quantum"-styled duplicate of
    // CanadaSelfEmployedAssessment (the honest, UPRM-wired "locked template" version).
    id: 'canada.self_employed_program',
    domain: 'canada',
    path: '/canada-self-employed-program',
    element: <Navigate to="/canada-self-employed-assessment" replace />,
  },
  {
    id: 'canada.quebec_skilled_worker',
    domain: 'canada',
    path: '/canada-quebec-skilled-worker',
    element: createProtectedRoute(<CanadaQuebecSkilledWorkerCalculator />, { tier: 'premium', meta: { title: 'Quebec Skilled Worker', region: 'CA', intent: 'calculator', toolId: 'ca-quebec-skilled-worker', category: 'calculators' } }),
  },
  {
    id: 'canada.pathway_engine',
    domain: 'canada',
    path: '/canada-pathway-engine',
    element: createProtectedRoute(<CanadaPathwayEngine />, { tier: 'premium', meta: { title: 'Pathway Engine', region: 'CA', intent: 'engine', toolId: 'ca-pathway-engine', category: 'assessments' } }),
  },
  {
    id: 'canada.timeline_forecaster',
    domain: 'canada',
    path: '/canada-timeline-forecaster',
    element: createProtectedRoute(<CanadaTimelineForecaster />, { tier: 'premium', meta: { title: 'Timeline Forecaster', region: 'CA', intent: 'forecaster', toolId: 'ca-timeline-forecaster', category: 'integration' } }),
  },
  {
    id: 'canada.success_prediction',
    domain: 'canada',
    path: '/canada-success-prediction',
    element: createProtectedRoute(<CanadaSuccessPredictionEngine />, { tier: 'premium', meta: { title: 'Success Prediction', region: 'CA', intent: 'prediction', toolId: 'ca-success-prediction', category: 'assessments' } }),
  },
  {
    id: 'canada.risk_assessment',
    domain: 'canada',
    path: '/canada-risk-assessment',
    element: createProtectedRoute(<CanadaRiskAssessment />, { tier: 'premium', meta: { title: 'Risk Assessment', region: 'CA', intent: 'assessment', toolId: 'ca-risk-assessment', category: 'assessments' } }),
  },
  {
    id: 'canada.journey_map',
    domain: 'canada',
    path: '/canada-journey-map',
    element: createProtectedRoute(<CanadaJourneyMap />, { tier: 'premium', meta: { title: 'Journey Map', region: 'CA', intent: 'map' } }),
  },
  {
    id: 'canada.saved_journey_maps',
    domain: 'canada',
    path: '/canada-saved-journey-maps',
    element: createProtectedRoute(<CanadaSavedJourneyMaps />, { tier: 'premium', meta: { title: 'Saved Journey Maps', region: 'CA', intent: 'storage', toolId: 'ca-saved-journey-maps', category: 'planning' } }),
  },
  {
    id: 'canada.tools_ecosystem',
    domain: 'canada',
    path: '/canada-tools-ecosystem',
    element: createProtectedRoute(<CanadaToolsEcosystem />, { tier: 'premium', meta: { title: 'Tools Ecosystem', region: 'CA', intent: 'dashboard' } }),
  },
  {
    id: 'canada.pr_companion',
    domain: 'canada',
    path: '/canada-pr-companion',
    element: createProtectedRoute(<CanadaPrCompanion />, {
      protected: true,
      meta: { title: 'Canada PR Companion', region: 'CA', intent: 'companion', toolId: 'ca-pr-companion', category: 'integration' },
    }),
  },
  {
    id: 'canada.companion_results',
    domain: 'canada',
    path: '/canada-companion-results',
    element: createProtectedRoute(<CanadaCompanionResults />, {
      protected: true,
      meta: { title: 'Canada Companion Results', region: 'CA', intent: 'results', toolId: 'ca-companion-results', category: 'integration' },
    }),
  },
  {
    id: 'canada.immigration_hub_alias',
    domain: 'canada',
    path: '/canada-immigration-hub',
    element: <Navigate to="/canada-immigration" replace />,
  },
  {
    // CanadaImmigrationFlightDeck.tsx is an early VisaGuideOS-era page full of
    // fabricated AI metrics (fake ITA-chance predictions, a scripted fake chat
    // pretending to analyze your profile, hardcoded "sample" journey history).
    // It's been superseded by the real UPRM-wired JourneyOS dashboard - redirect
    // here rather than keep patching a page that's already been replaced.
    id: 'canada.flight_deck',
    domain: 'canada',
    path: '/canada-immigration-flight-deck',
    element: <Navigate to="/journey" replace />,
  },
  {
    id: 'canada.visa_form',
    domain: 'canada',
    path: '/canada-visa-form',
    element: createProtectedRoute(<CanadaVisaFormConductor />, { tier: 'premium', meta: { title: 'Visa Form', region: 'CA', intent: 'form', toolId: 'ca-visa-form', category: 'documents' } }),
  },
  {
    id: 'canada.evisa_gateway',
    domain: 'canada',
    path: '/canada-evisa-gateway',
    element: createProtectedRoute(<CanadaEvisaGateway />, { tier: 'premium', meta: { title: 'eVisa Gateway', region: 'CA', intent: 'gateway' } }),
  },
  {
    id: 'canada.dependency_commander',
    domain: 'canada',
    path: '/canada-dependency-commander',
    element: createProtectedRoute(<CanadaDependencyCommander />, { tier: 'premium', meta: { title: 'Dependency Commander', region: 'CA', intent: 'tool' } }),
  },
  {
    id: 'canada.assessment_result',
    domain: 'canada',
    path: '/canada-assessment-result-analysis',
    element: createProtectedRoute(<CanadaAssessmentResultAnalysis />, { tier: 'premium', meta: { title: 'Assessment Result Analysis', region: 'CA', intent: 'analysis' } }),
  },
  {
    id: 'canada.doc_completeness',
    domain: 'canada',
    path: '/canada-document-completeness-checker',
    element: createProtectedRoute(<CanadaDocumentCompletenessChecker />, { tier: 'premium', meta: { title: 'Document Completeness', region: 'CA', intent: 'checker', toolId: 'ca-doc-completeness', category: 'documents' } }),
  },
  {
    id: 'canada.translation_guide',
    domain: 'canada',
    path: '/canada-translation-guide',
    element: createProtectedRoute(<CanadaTranslationGuide />, { tier: 'premium', meta: { title: 'Translation Guide', region: 'CA', intent: 'guide', toolId: 'ca-translation-guide', category: 'documents' } }),
  },
  {
    id: 'canada.eca_tool',
    domain: 'canada',
    path: '/canada-eca-tool',
    element: createProtectedRoute(<CanadaECATool />, { tier: 'premium', meta: { title: 'ECA Tool', region: 'CA', intent: 'tool', toolId: 'ca-eca-tool', category: 'documents' } }),
  },
  {
    id: 'canada.clb_converter',
    domain: 'canada',
    path: '/canada-clb-converter',
    element: createProtectedRoute(<CanadaCLBConverter />, { tier: 'premium', meta: { title: 'CLB Converter', region: 'CA', intent: 'converter', toolId: 'ca-clb-converter', category: 'calculators' } }),
  },
  {
    id: 'canada.work_ref_checker',
    domain: 'canada',
    path: '/canada-work-ref-letter-checker',
    element: createProtectedRoute(<CanadaWorkRefLetterChecker />, { tier: 'premium', meta: { title: 'Work Ref Checker', region: 'CA', intent: 'validator', toolId: 'ca-work-ref-checker', category: 'validators' } }),
  },
  {
    id: 'canada.tax_estimator',
    domain: 'canada',
    path: '/canada-tax-estimator',
    element: createProtectedRoute(<CanadaTaxEstimator />, { tier: 'premium', meta: { title: 'Tax Estimator', region: 'CA', intent: 'calculator', toolId: 'ca-tax-estimator', category: 'financial' } }),
  },
  {
    id: 'canada.health_coverage',
    domain: 'canada',
    path: '/canada-health-coverage',
    element: createProtectedRoute(<CanadaHealthCoverageChecker />, { tier: 'premium', meta: { title: 'Health Coverage', region: 'CA', intent: 'checker', toolId: 'ca-health-coverage', category: 'financial' } }),
  },
  {
    id: 'canada.citizenship_eligibility',
    domain: 'canada',
    path: '/canada-citizenship-eligibility',
    element: createProtectedRoute(<CanadaCitizenshipEligibility />, { tier: 'premium', meta: { title: 'Citizenship Eligibility', region: 'CA', intent: 'checker', toolId: 'ca-citizenship-eligibility', category: 'planning' } }),
  },
  {
    id: 'canada.province_comparison',
    domain: 'canada',
    path: '/canada-province-comparison',
    element: createProtectedRoute(<CanadaProvinceComparisonTool />, { tier: 'premium', meta: { title: 'Province Comparison', region: 'CA', intent: 'comparison', toolId: 'ca-province-comparison', category: 'planning' } }),
  },
  {
    id: 'canada.lmia_employer_db',
    domain: 'canada',
    path: '/canada-lmia-employer-database',
    element: createProtectedRoute(<CanadaLMIAEmployerDatabase />, { tier: 'premium', meta: { title: 'LMIA Employer DB', region: 'CA', intent: 'database', toolId: 'ca-lmia-employer', category: 'planning' } }),
  },
  {
    id: 'canada.job_bank',
    domain: 'canada',
    path: '/canada-job-bank',
    element: createProtectedRoute(<CanadaJobBankIntegration />, { tier: 'premium', meta: { title: 'Job Bank', region: 'CA', intent: 'tool', toolId: 'ca-job-bank', category: 'integration' } }),
  },
  {
    id: 'canada.business_visitor_ceta',
    domain: 'canada',
    path: '/canada-business-visitor-ceta-guide',
    element: createProtectedRoute(<CanadaBusinessVisitorCETAGuide />, { tier: 'premium', meta: { title: 'Business Visitor & CETA', region: 'CA', intent: 'guide', toolId: 'ca-business-visitor', category: 'planning' } }),
  },
  {
    id: 'canada.draw_predictor',
    domain: 'canada',
    path: '/canada-express-entry-draw-predictor',
    element: createProtectedRoute(<CanadaExpressEntryDrawPredictor />, { tier: 'premium', meta: { title: 'Draw Predictor', region: 'CA', intent: 'predictor', toolId: 'ca-ee-draw-predictor', category: 'integration' } }),
  },
  {
    id: 'canada.pnp_processing_times',
    domain: 'canada',
    path: '/canada-pnp-processing-times',
    element: createProtectedRoute(<CanadaPNPProcessingTimes />, { tier: 'premium', meta: { title: 'PNP Processing Times', region: 'CA', intent: 'tool', toolId: 'ca-pnp-timeline', category: 'integration' } }),
  },
  {
    id: 'canada.webform_helper',
    domain: 'canada',
    path: '/canada-ircc-webform-helper',
    element: createProtectedRoute(<CanadaIRCCWebformHelper />, { tier: 'premium', meta: { title: 'IRCC Webform Helper', region: 'CA', intent: 'guide', toolId: 'ca-ircc-webform', category: 'integration' } }),
  },
  {
    id: 'canada.physician_finder',
    domain: 'canada',
    path: '/canada-panel-physician-finder',
    element: createProtectedRoute(<CanadaPanelPhysicianFinder />, { tier: 'premium', meta: { title: 'Panel Physician Finder', region: 'CA', intent: 'finder', toolId: 'ca-panel-physician', category: 'integration' } }),
  },
  {
    id: 'canada.pr_photo_validator',
    domain: 'canada',
    path: '/canada-pr-card-photo-validator',
    element: createProtectedRoute(<CanadaPRCardPhotoValidator />, { tier: 'premium', meta: { title: 'PR Card Photo Validator', region: 'CA', intent: 'validator', toolId: 'ca-pr-photo-validator', category: 'validators' } }),
  },
  {
    id: 'canada.ita_simulator',
    domain: 'canada',
    path: '/canada-ita-simulator',
    element: createProtectedRoute(<CanadaITASimulator />, { tier: 'premium', meta: { title: 'ITA Simulator', region: 'CA', intent: 'simulator', toolId: 'ca-ita-simulator', category: 'assessments' } }),
  },
  {
    id: 'canada.eligibility_quiz',
    domain: 'canada',
    path: '/canada-immigration-eligibility-quiz',
    element: createProtectedRoute(<CanadaImmigrationEligibilityQuiz />, { tier: 'premium', meta: { title: 'Eligibility Quiz', region: 'CA', intent: 'assessment', toolId: 'ca-eligibility-quiz', category: 'assessments' } }),
  },
  {
    id: 'canada.points_calculator',
    domain: 'canada',
    path: '/canada-comprehensive-points-calculator',
    element: createProtectedRoute(<CanadaComprehensivePointsCalculator />, { tier: 'premium', meta: { title: 'Points Calculator', region: 'CA', intent: 'calculator', toolId: 'ca-comprehensive-points', category: 'calculators' } }),
  },
  {
    id: 'canada.maintenance_funds',
    domain: 'canada',
    path: '/canada-maintenance-funds-calculator',
    // Tool-usage tracking pilot (see docs on RouteMeta.toolId) — matches the
    // CanadaToolsEcosystem.tsx catalog id 'maintenance-funds'.
    element: createProtectedRoute(<CanadaMaintenanceFundsCalculator />, { tier: 'premium', meta: { title: 'Maintenance Funds', region: 'CA', intent: 'calculator', toolId: 'ca-maintenance-funds', category: 'financial' } }),
  },
  {
    id: 'canada.dependent_visa_calc',
    domain: 'canada',
    path: '/canada-dependent-visa-calculator',
    element: createProtectedRoute(<CanadaDependentVisaCalculator />, { tier: 'premium', meta: { title: 'Dependent Visa Calculator', region: 'CA', intent: 'calculator', toolId: 'ca-dependent-visa', category: 'calculators' } }),
  },
  {
    id: 'canada.financial_tools',
    domain: 'canada',
    path: '/canada-financial-maintenance-tools',
    element: createProtectedRoute(<CanadaFinancialMaintenanceTools />, { tier: 'premium', meta: { title: 'Financial Tools', region: 'CA', intent: 'tool' } }),
  },
  {
    id: 'canada.dli_validator',
    domain: 'canada',
    path: '/canada-dli-validator',
    element: createProtectedRoute(<CanadaDLIValidator />, { tier: 'premium', meta: { title: 'DLI Validator', region: 'CA', intent: 'validator', toolId: 'ca-dli-validator', category: 'validators' } }),
  },
  {
    id: 'canada.accommodation_checker',
    domain: 'canada',
    path: '/canada-accommodation-checker',
    element: createProtectedRoute(<CanadaAccommodationChecker />, { tier: 'premium', meta: { title: 'Accommodation Checker', region: 'CA', intent: 'checker', toolId: 'ca-accommodation-checker', category: 'planning' } }),
  },
  {
    id: 'canada.biometric_validator',
    domain: 'canada',
    path: '/canada-biometric-validator',
    element: createProtectedRoute(<CanadaBiometricValidator />, { tier: 'premium', meta: { title: 'Biometric Validator', region: 'CA', intent: 'validator', toolId: 'ca-biometric-validator', category: 'validators' } }),
  },
  {
    id: 'canada.pathway_analytics',
    domain: 'canada',
    path: '/canada-pathway-analytics',
    element: createProtectedRoute(<CanadaPathwayAnalyticsDashboard />, { tier: 'premium', meta: { title: 'Pathway Analytics', region: 'CA', intent: 'dashboard', toolId: 'ca-pathway-analytics', category: 'assessments' } }),
  },
  {
    id: 'canada.pathway_dreams',
    domain: 'canada',
    path: '/canada-pathway-dreams',
    element: createProtectedRoute(<CanadaPathwayDreamsEngine />, { tier: 'premium', meta: { title: 'Pathway Dreams', region: 'CA', intent: 'engine' } }),
  },
  {
    id: 'canada.occupation_demand',
    domain: 'canada',
    path: '/canada-occupation-demand-checker',
    element: createProtectedRoute(<CanadaOccupationDemandChecker />, { tier: 'premium', meta: { title: 'Occupation Demand', region: 'CA', intent: 'checker', toolId: 'ca-occupation-demand', category: 'assessments' } }),
  },
  {
    id: 'canada.startup_planner',
    domain: 'canada',
    path: '/canada-startup-visa-planner',
    element: createProtectedRoute(<CanadaStartupVisaPlanner />, { tier: 'premium', meta: { title: 'Startup Planner', region: 'CA', intent: 'planner' } }),
  },
  {
    // CanadaTemporaryWorkerRoutes.tsx archived — old "Quantum"-styled duplicate of
    // CanadaWorkerAssessment (the honest, UPRM-wired "locked template" version).
    id: 'canada.temp_worker_routes',
    domain: 'canada',
    path: '/canada-temporary-worker-routes',
    element: <Navigate to="/canada-worker-assessment" replace />,
  },
  {
    id: 'canada.tech_data',
    domain: 'canada',
    path: '/canada-immigration-tech-data',
    element: createProtectedRoute(<CanadaImmigrationTechData />, { tier: 'premium', meta: { title: 'Immigration Tech Data', region: 'CA', intent: 'data', toolId: 'ca-immigration-tech-data', category: 'integration' } }),
  },
  {
    id: 'canada.youth_student',
    domain: 'canada',
    path: '/canada-youth-student-tools',
    element: createProtectedRoute(<CanadaYouthStudentTools />, { tier: 'premium', meta: { title: 'Youth & Student Tools', region: 'CA', intent: 'tool', toolId: 'ca-youth-student', category: 'planning' } }),
  },
  {
    id: 'canada.healthcare_routes',
    domain: 'canada',
    path: '/canada-professional-healthcare-routes',
    element: createProtectedRoute(<CanadaProfessionalHealthcareRoutes />, { tier: 'premium', meta: { title: 'Healthcare Routes', region: 'CA', intent: 'guide', toolId: 'ca-healthcare-routes', category: 'assessments' } }),
  },
  {
    id: 'canada.housing_tools',
    domain: 'canada',
    path: '/canada-accommodation-housing',
    element: createProtectedRoute(<CanadaAccommodationHousing />, { tier: 'premium', meta: { title: 'Housing Tools', region: 'CA', intent: 'tool', toolId: 'ca-accommodation-housing', category: 'planning' } }),
  },
  {
    id: 'canada.family_tools',
    domain: 'canada',
    path: '/canada-dependant-family-tools',
    element: createProtectedRoute(<CanadaDependantFamilyTools />, { tier: 'premium', meta: { title: 'Family Tools', region: 'CA', intent: 'tool', toolId: 'ca-dependant-family', category: 'planning' } }),
  },
  {
    id: 'canada.expiry_tools',
    domain: 'canada',
    path: '/canada-visa-expiry-extension',
    element: createProtectedRoute(<CanadaVisaExpiryExtensionTools />, { tier: 'premium', meta: { title: 'Visa Expiry Tools', region: 'CA', intent: 'tool', toolId: 'ca-visa-expiry-extension', category: 'planning' } }),
  },
  {
    id: 'canada.pr_tools',
    domain: 'canada',
    path: '/canada-permanent-residence-tools',
    element: createProtectedRoute(<CanadaPermanentResidenceTools />, { tier: 'premium', meta: { title: 'PR Tools', region: 'CA', intent: 'tool', toolId: 'ca-pr-tools', category: 'assessments' } }),
  },
  {
    id: 'canada.global_salary',
    domain: 'canada',
    path: '/canada-global-salary-calculator',
    element: createProtectedRoute(<CanadaGlobalSalaryCalculator />, { tier: 'premium', meta: { title: 'Global Salary', region: 'CA', intent: 'calculator', toolId: 'ca-global-salary', category: 'financial' } }),
  },
  {
    id: 'canada.visa_fee_calc',
    domain: 'canada',
    path: '/canada-visa-fee-calculator',
    element: createProtectedRoute(<CanadaVisaFeeCalculator />, { tier: 'premium', meta: { title: 'Visa Fee Calculator', region: 'CA', intent: 'calculator', toolId: 'ca-visa-fee-calc2', category: 'financial' } }),
  },

  // Document validators
  {
    id: 'canada.police_validator',
    domain: 'canada',
    path: '/canada-police-validator',
    element: createProtectedRoute(<CanadaPoliceValidator />, { tier: 'premium', meta: { title: 'Police Validator', region: 'CA', intent: 'validator', toolId: 'ca-police-validator', category: 'validators' } }),
  },
  {
    id: 'canada.marriage_validator',
    domain: 'canada',
    path: '/canada-marriage-validator',
    element: createProtectedRoute(<CanadaMarriageValidator />, { tier: 'premium', meta: { title: 'Marriage Validator', region: 'CA', intent: 'validator', toolId: 'ca-marriage-validator', category: 'validators' } }),
  },
  {
    id: 'canada.education_validator',
    domain: 'canada',
    path: '/canada-education-validator',
    element: createProtectedRoute(<CanadaEducationValidator />, { tier: 'premium', meta: { title: 'Education Validator', region: 'CA', intent: 'validator', toolId: 'ca-education-validator', category: 'validators' } }),
  },
  {
    id: 'canada.verification_dashboard',
    domain: 'canada',
    path: '/canada-verification-dashboard',
    element: createProtectedRoute(<CanadaVerificationDashboard />, { tier: 'premium', meta: { title: 'Verification Dashboard', region: 'CA', intent: 'dashboard', toolId: 'ca-verification-dashboard', category: 'validators' } }),
  },
  {
    id: 'canada.bank_validator',
    domain: 'canada',
    path: '/canada-bank-validator',
    element: createProtectedRoute(<CanadaBankValidator />, { tier: 'premium', meta: { title: 'Bank Validator', region: 'CA', intent: 'validator', toolId: 'ca-bank-validator', category: 'validators' } }),
  },
  {
    id: 'canada.document_authentication',
    domain: 'canada',
    path: '/canada-document-authentication',
    element: createProtectedRoute(<CanadaDocumentAuthentication />, { tier: 'premium', meta: { title: 'Document Authentication', region: 'CA', intent: 'guide', toolId: 'ca-document-authentication', category: 'documents' } }),
  },

  // ---- Broken-link redirects (2026-09-28 scan) ----
  { id: 'canada.redirect.caregiver_short', domain: 'canada', path: '/canada-caregiver', element: <Navigate to="/canada-caregiver-assessment" replace /> },
  { id: 'canada.redirect.immigration_programs', domain: 'canada', path: '/canada-immigration-programs', element: <Navigate to="/canada-permanent-residence-tools" replace /> },
  { id: 'canada.redirect.student_loan_matcher', domain: 'canada', path: '/canada-student-loan-matcher', element: <Navigate to="/canada-youth-student-tools" replace /> },
  { id: 'canada.redirect.visitor_expansion', domain: 'canada', path: '/canada-visitor-expansion', element: <Navigate to="/canada-business-visitor-ceta-guide" replace /> },
  { id: 'canada.redirect.doc_intel_scanner', domain: 'canada', path: '/canada/document-intelligence-scanner', element: <Navigate to="/canada-document-completeness-checker" replace /> },
  { id: 'canada.redirect.english_assessor', domain: 'canada', path: '/canada/english-language-assessor', element: <Navigate to="/canada-clb-converter" replace /> },
  { id: 'canada.redirect.ee_crs_slash', domain: 'canada', path: '/canada/express-entry-crs', element: <Navigate to="/canada-express-entry-crs" replace /> },
  { id: 'canada.redirect.lmia_slash', domain: 'canada', path: '/canada/lmia-employer-database', element: <Navigate to="/canada-lmia-employer-database" replace /> },
  { id: 'canada.redirect.rnip_community', domain: 'canada', path: '/canada/rnip-community-finder', element: <Navigate to="/canada-rnip-assessment" replace /> },
  { id: 'canada.redirect.rnip_eligibility', domain: 'canada', path: '/canada/rnip-eligibility', element: <Navigate to="/canada-rnip-assessment" replace /> },
  { id: 'canada.redirect.rnip_stream', domain: 'canada', path: '/canada/rnip-stream-matcher', element: <Navigate to="/canada-rnip-assessment" replace /> },
];
